import { Bell, CalendarCheck, CheckSquare, Globe2, Home, Mail, MessageSquareText, PhoneCall, Settings2, StickyNote, Users } from "lucide-react";

export const STAGES = [
  { id: "new", label: "New lead", color: "#2563EB" },
  { id: "attempted", label: "Attempted contact", color: "#0EA5E9" },
  { id: "contacted", label: "Spoke with lead", color: "#0D9488" },
  { id: "appointment", label: "Appointment set", color: "#F59E0B" },
  { id: "tour", label: "Showing homes", color: "#C8961E" },
  { id: "offer", label: "Submitting offers", color: "#EA580C" },
  { id: "contract", label: "Under contract", color: "#16A34A" },
  { id: "closed", label: "Closed", color: "#047857" },
  { id: "nurture", label: "Nurture", color: "#64748B" },
  { id: "trash", label: "Trash", color: "#94A3B8" },
];
export const PIPELINE = STAGES.filter((s) => !["nurture", "trash"].includes(s.id));
export const stageOf = (id) => STAGES.find((s) => s.id === id) || STAGES[0];
export const LEAD_TYPES = ["buyer", "seller", "both", "renter", "investor"];
export const RATINGS = [["", "Unrated"], ["hot", "Hot"], ["warm", "Warm"], ["cold", "Cold"]];
export const BRANCHES = ["", "Navy", "Marine Corps", "Army", "Air Force", "Coast Guard", "Space Force", "Veteran", "DoD Civilian"];
export const TIMEFRAMES = ["", "ASAP", "0-3 months", "3-6 months", "6-12 months", "12+ months"];
export const CALL_OUTCOMES = ["Talked", "Left message", "No answer", "Bad number"];

export const TASK_KINDS = { follow_up: ["Follow up", Bell], call: ["Call", PhoneCall], email: ["Email", Mail], text: ["Text", MessageSquareText], showing: ["Showing", Home], other: ["To-do", CheckSquare] };
export const ACTIVITY = {
  note: { label: "Note", icon: StickyNote, color: "#C8961E" }, call: { label: "Call", icon: PhoneCall, color: "#16A34A" },
  email: { label: "Email", icon: Mail, color: "#2563EB" }, text: { label: "Text", icon: MessageSquareText, color: "#0EA5E9" },
  meeting: { label: "Meeting", icon: Users, color: "#7C5E10" }, appointment: { label: "Appointment", icon: CalendarCheck, color: "#F59E0B" },
  task: { label: "Task", icon: CheckSquare, color: "#475569" }, system: { label: "Update", icon: Settings2, color: "#94A3B8" },
  web: { label: "Website", icon: Globe2, color: "#E11D48" },
};

const DAY = 86400000;
const active = (l) => !["closed", "trash"].includes(l.stage);
export const SMART_LISTS = [
  { id: "all", label: "All people", test: (l) => l.stage !== "trash" },
  { id: "new24", label: "New leads · 24h", test: (l) => Date.now() - new Date(l.created_at) < DAY },
  { id: "unassigned", label: "Unassigned", owner: true, test: (l) => !l.assigned_agent_id && l.stage !== "trash" },
  { id: "uncontacted", label: "Never contacted", test: (l) => !l.last_contacted_at && active(l) },
  { id: "hot", label: "Hot leads", test: (l) => l.rating === "hot" && active(l) },
  { id: "stale", label: "No contact 7+ days", test: (l) => l.last_contacted_at && Date.now() - new Date(l.last_contacted_at) > 7 * DAY && active(l) },
  { id: "website", label: "Website leads", test: (l) => (l.source || "").startsWith("Website") && l.stage !== "trash" },
  { id: "military", label: "Military · PCS", test: (l) => (l.military_branch || l.pcs_date || (l.tags || []).some((t) => /pcs|military/i.test(t))) && l.stage !== "trash" },
  { id: "buyers", label: "Buyers", test: (l) => ["buyer", "both"].includes(l.lead_type) && active(l) },
  { id: "sellers", label: "Sellers", test: (l) => ["seller", "both"].includes(l.lead_type) && active(l) },
  { id: "appointments", label: "Appointment set", test: (l) => ["appointment", "tour"].includes(l.stage) },
  { id: "contract", label: "Offers & contracts", test: (l) => ["offer", "contract"].includes(l.stage) },
  { id: "nurture", label: "Nurture", test: (l) => l.stage === "nurture" },
  { id: "past", label: "Past clients", test: (l) => l.stage === "closed" },
  { id: "trash", label: "Trash", test: (l) => l.stage === "trash" },
];

export const leadName = (l) => (l ? `${l.first_name || ""} ${l.last_name || ""}`.trim() : "");
export const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";
export const ago = (iso) => {
  if (!iso) return "Never";
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "Just now"; if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
export const money = (n) => (!n ? "—" : n >= 1e6 ? `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 2)}M` : `$${Math.round(n / 1000)}K`);
export const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "No date");
export const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export const isToday = (iso) => !!iso && sameDay(new Date(iso), new Date());
export const isOverdue = (iso) => !!iso && new Date(iso) < new Date() && !isToday(iso);
export const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
export const fromLocalInput = (value) => (value ? new Date(value).toISOString() : "");
